import { useEffect, useReducer, useState } from "react";
import GTable from "../design/components/GTable";
import Button from "../design/Button";
import GModal from "../design/components/GModal";
import { Autocomplete, ListItem, TextField } from "@mui/material";
import { JService } from "../api-service/ApiService";
import { Box } from "@mui/system";
import userEditIcon from "../../assets/call-logs-edit.svg";
import { useDispatch, useSelector } from "react-redux";
import { setVirtualNumber } from "../features/virtual-number/virtualNumber";
import { topNavAccountSelectedAccountTickIcon } from "../../utility-functions/assetLoader";
import commonStyles from "../design/commonStyles";
import { enqueueSnackbar } from "notistack";
import { fetchData, filtersReducer } from "../../utility-functions";
import { Filters } from "../dashboard/Filters";
import { SelectedUsersContainer } from "../users-and-teams/components";

const filtersList = [
  { name: "Role", toggle: false, payloadFor: 'roles', isSearch: false, permission: 'manage roles'}
]
const API_BASE_URL = process.env.REACT_APP_BETA;

export default function OutgoingTab(props) {
  const { originalSettings, setOriginalSettings, showLoader, refreshHandler } = props;
  const dispatch = useDispatch();

  const accountDetails = useSelector((state) => state.account.value);
  const { canManageRoles } = useSelector((state) => state?.auth?.permissions);

  const [rows, setRows] = useState([]);
  const [rowsToDisplay, setRowsToDisplay] = useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserOptions, setAddUserOptions] = useState([]);
  const [outboundUserIds, setOutboundUserIds] = useState([]);

  const [rowBeingEdited, setRowBeingEdited] = useState(null);
  const [rowOptionsCords, setRowOptionsCords] = useState([0, 0]);
  const [showRemoveUserPrompt, setShowRemoveUserPrompt] = useState(false);

  const [csvData, setCSVData] = useState({
    headers: [["User"], ["Roles"], ["Teams"]],
    data: [],
    ids: [],
  });

  const initialState = {
    roles: {
      all: [],
      selected: [],
    },
    teams: {
      all: originalSettings?.all_teams?.map((team) => ({
        id: team.id,
        name: team.name,
      })),
      selected: [],
    },
    // currentSet: "",
    searchText: "",
    searchFields: [],
  };

  const [availableData, dispatchAvailableData] = useReducer(
    filtersReducer,
    initialState,
  );

  useEffect(() => {
    setRows(originalSettings.outbound_users);
    setRowsToDisplay(originalSettings.outbound_users);

    const _outboundUserIds = originalSettings.outbound_users.map(
      (user) => user.id,
    );
    const allUsers = {}
    originalSettings.all_users.forEach(user => {
      allUsers[user.id] = user
    })
    setAddUserOptions(allUsers);
    setOutboundUserIds(_outboundUserIds);
  }, [originalSettings]);

  // Use Effect for CSV download
  useEffect(() => {
    setCSVData((prevState) => {
      let newIds = [];
      let newData = [];

      for (let i = 0; i < rowsToDisplay.length; i++) {
        let item = rowsToDisplay[i];
        if (!newIds?.includes(item?.id)) {
          newIds.push(item?.id);
          let temp = [
            item.first_name + " " + item.last_name + " (" + item.email + ")",
            item.roles?.map((role) => role.title)?.join(", "),
            item.teams
              .map((team) => {
                return team.name;
              })
              .join(", "),
          ];
          newData.push(temp);
        }
      }
      return { ...prevState, data: newData, ids: newIds };
    });
  }, [rowsToDisplay]);

  // UseEffect for Search and Filters
  useEffect(() => {
    onSearch(availableData?.searchText);
  }, [
    availableData?.searchText,
    availableData?.roles?.selected,
    availableData?.teams?.selected,
    rows,
  ]);

  // UseEffect to hide three dots menu on scroll
  useEffect(() => {
    const handleScrollWhenRowOptionsOpen = (e) => {
      setRowBeingEdited(null);

      window.removeEventListener("scroll", handleScrollWhenRowOptionsOpen);
    };
    if (rowBeingEdited) {
      window.addEventListener("scroll", handleScrollWhenRowOptionsOpen);
    }

    return () => {
      window.removeEventListener("scroll", handleScrollWhenRowOptionsOpen);
    };
  }, [rowBeingEdited]);

  useEffect(() => {
    const setRoles = (roles) => {
      dispatchAvailableData({
        type: "setPayloadToAllOfType",
        payload: {
          payload: roles?.map(({ id, title }) => ({ id, name: title })),
          payloadFor: "roles",
        },
      });
    };
    if (canManageRoles) {
      fetchData("/api/v1/auth/roles/", setRoles, "roles");
    }
  }, [canManageRoles]);

  const onSearch = (searchText) => {
    if (!searchText?.trim()) {
      combineSearchAndFilters(originalSettings.outbound_users);
      return;
    }
    const searchTextLower = searchText?.toLowerCase();
    const temp = originalSettings.outbound_users?.filter(
      (row) =>
        (row?.first_name + " " + row?.last_name)
          .toLowerCase()
          .includes(searchTextLower) ||
        row?.email.toLowerCase().includes(searchTextLower),
    );
    combineSearchAndFilters(temp);
  };

  const combineSearchAndFilters = (arg1) => {
    // if filters and search is empty
    if (
      !availableData?.teams?.selected?.length &&
      !availableData?.roles?.selected?.length &&
      availableData?.searchText?.trim()?.length === 0
    ) {
      if (arg1 != rowsToDisplay) {
        setRowsToDisplay(arg1); //eslint-disable-line
      }
      return;
    }

    // teams selected changed
    if (availableData?.teams?.selected?.length) {
      arg1 = arg1?.filter(
        (mr) =>
          mr?.teams
            ?.map((ut) => ut?.id)
            ?.findIndex((t) => availableData?.teams?.selected?.includes(t)) >=
          0,
      );
    }

    // roles selected changes
    if (availableData?.roles?.selected?.length) {
      arg1 = arg1?.filter((te) => {
        return (
          te?.roles
            ?.map((role) => role?.id)
            ?.findIndex((id) =>
              availableData?.roles?.selected?.includes(id),
            ) !== -1
        );
      });
    }

    setRowsToDisplay(arg1);
  };

  const refreshSelfVirtualNumbers = () => {
    JService.get(API_BASE_URL + "/api/v1/auth/retrieve-virtual-numbers")
      .then((response) => {
        if (response?.success) {
          dispatch(setVirtualNumber(response.data));
          console.log("refreshed users virtual numbers");
        }
      })
      .catch((error) => {
        console.error(error.response);
      });
  };

  const updateOutboundUsers = (action = "add", userIdToRemove) => {
    let url, payload;

    if (originalSettings.type === "Calling") {
      url = `/api/v1/auth/virtual-numbers/calling/${originalSettings.id}/`;
      payload = {
        outbound_user_ids: outboundUserIds,
      };
      if(action === "remove" && userIdToRemove) {
        payload = {
          outbound_user_ids: outboundUserIds?.filter(id => id !== userIdToRemove),
        }
      }
    } 
    // else if (originalSettings.type === "Voice Broadcast") {
    //   url = `/api/v1/auth/virtual-numbers/voice-broadcast/${originalSettings.id}/`;
    //   payload = {
    //     user_ids: updatedValue,
    //   };
    // } 
    else return;

    JService.patch(url, payload)
      .then((res) => {
        if (!res?.success)
          throw new Error(
            res?.message || "Error white updating virtual number",
          );

		setOriginalSettings(res.data);
		dispatchAvailableData({ type: "clearAllFilters", payload: null });
		
		// refresh virtual numbers -> if user being updated is the logged in user
        refreshSelfVirtualNumbers();
        enqueueSnackbar(res?.message, { variant: "success"})
      })
      .catch((err) => enqueueSnackbar(err?.message, { variant: "error" }))
      .finally(() => {
        setShowRemoveUserPrompt(false);
        setShowAddUserModal(false);
      });
  };

  const cols = [
    {
      field: "userName",
      headerName: "User",
      headerClassName: "user-table-header-user-name",
      flex: 1,
      renderCell: (params) => {
        return (
          <div style={{ marginLeft: "14px" }}>
            <p
              className="t7 regular-font nc-gray-900"
              style={{ margin: 0, fontSize: "14px" }}
            >
              {params.row.first_name + " " + params.row.last_name}
            </p>
            <p
              className="t7 regular-font nc-gray-600"
              style={{ margin: 0, fontSize: "14px" }}
            >
              {params.row.email}
            </p>
          </div>
        );
      },
    },
    {
      field: "role",
      headerName: "Roles",
      flex: 0.8,
      renderCell: (params) => {
        return (
          <p
            className="t7 regular-font nc-gray-900"
            style={{ fontSize: "14px" }}
          >
            {params.row.roles?.map((role) => role.title)?.join(", ")}
          </p>
        );
      },
    },
    {
      field: "outboundUsersEdit",
      headerName: "",
      flex: 0.5,
      cellClassName: "table-cell-edit", // @important use this threedots
      renderCell: (params) => {
        return (
          <div>
            <Box>
              <img
                onClick={(e) => {
                  setRowOptionsCords([e["clientX"], e["clientY"]]);
                  e.stopPropagation();
                  setRowBeingEdited(params.row);
                }}
                src={userEditIcon}
                alt="three dots icon"
              />
            </Box>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div style={localStyles.container}>
        <Filters
          availableData={availableData}
          dispatchAvailableData={dispatchAvailableData}
          fromScreen="virtual-number-outgoing"
          dateFilter={false}
          filtersList={filtersList}
          style={{ marginTop: 0, marginBottom: 0 }}
        />
        <Button
          variant="filled"
          buttonText="Add user"
          disabled={false}
          styleOverride={localStyles.buttonStyles}
          isLoading={false}
          hierarchy="green"
          onClick={() => setShowAddUserModal(true)}
        />
      </div>
      <GTable
        leftHeader={{
          filters: [],
          isSearch: true
        }}
        rightHeader={{ isRefresh: true, isDownload: true }}
        refreshHandler={refreshHandler}
        download={{
          data: csvData,
          body: null,
          filename:
            `${originalSettings.name || ""}-outgoing-users` +
            new Date().toLocaleString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }),
        }}
        availableData={availableData}
        dispatchAvailableData={dispatchAvailableData}
        fromScreen="virtual-number-outgoing"
        onSearch={onSearch}
        rows={rowsToDisplay}
        columns={cols}
        useMuiTable={true}
        isLoading={showLoader}
        getTableRowClassName={() => `teams-table-row`}
        additionalProps={{ bgHover: true }}
        onRowClick={() => {}}
        styleOverride={{ marginTop: 24 }}
      />

      {!!rowBeingEdited && (
        <div
          className="row-options-backdrop"
          onClick={() => {
            setRowBeingEdited(null);
          }}
        >
          <div style={{ position: "relative" }}>
            <div
              className="row-options-foreground"
              style={{
                marginLeft: rowOptionsCords[0],
                marginTop: rowOptionsCords[1],
              }}
            >
              <button
                style={{ textAlign: "left" }}
                className="t6 nc-gray-900"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRemoveUserPrompt(true);
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      <GModal
        modalStyle={localStyles.addUserModalStyle}
        closeModal={() => setShowAddUserModal(false)}
        heading={"Add user for outgoing call access"}
        visible={showAddUserModal}
        bottomButtonsStyle={{ borderTop: "none", paddingTop: "0" }}
        bottomButtons={[
          <Button
            variant="outlined"
            buttonText="Cancel"
            icon={{}}
            disabled={false}
            isLoading={false}
            hierarchy="white"
            onClick={() => {
              setShowAddUserModal(false);
              setOutboundUserIds(originalSettings.outbound_users.map((user) => user.id))
            }}
          />,
          <Button
            variant="filled"
            buttonText={"Add"}
            icon={{}}
            isLoading={false}
            hierarchy="green"
            disabled={false}
            onClick={updateOutboundUsers}
          />,
        ]}
        body={
          <form>
            <div>
              <Autocomplete
                forcePopupIcon={false}
                multiple
                disableCloseOnSelect
                disableClearable
                sx={{
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #34CB65 !important",
                  },
                  marginTop: '24px'
                }}
                size="small"
                ListboxProps={{sx: {maxHeight: '206px'}}}
                options={Object.values(addUserOptions)}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name} ${option.email}`}
                value={outboundUserIds}
                onChange={(e, values) => setOutboundUserIds(values?.map(elem => typeof elem === "object" ? elem.id : elem))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Enter name to add user"
                    variant="outlined"
                  />
                )}
                renderTags={() => ""}
                renderOption={(props, option) => {
                  const selected = outboundUserIds?.includes(option.id);
                  return (
                  <ListItem
                    {...props}
                    key={option.id}
                    sx={{
                      backgroundColor: selected ? "#F0FCF4 !important" : "white",
                      borderRadius: "5px",
                      "&:hover": { backgroundColor: "#F0FCF4 !important" },
                    }}
                  >
                    <Box sx={{ flexGrow: 1, padding: '12px 20px', ...localStyles.flexRow}}>
                     <div>
                      <p className="t7 regular-font nc-gray-900">{option.first_name + ' ' + option.last_name}</p>
                      <p className="t7 regular-font nc-gray-500" style={{marginTop: 0}}>{option.email}</p>
                     </div>
                     <span>{selected && topNavAccountSelectedAccountTickIcon()}</span>
                    </Box>
                  </ListItem>
                )}}
              />
              <Box
                className="custom-scrollbar"
                sx={localStyles.usersContainer}
              > 
                <SelectedUsersContainer
                  selectedUsers={outboundUserIds}
                  allUsers={addUserOptions}
                  handleRemoveUser={(removedValue) => 
                    setOutboundUserIds(prev => {
                      let temp = [...prev];
                      temp = temp.filter(id => id !== removedValue);
                      return temp;
                  })}
                  noSelectionText="No users selected"
                />
              </Box>
            </div>
          </form>
        }
      />

      <GModal
        modalStyle={localStyles.removeUserModalStyle}
        closeModal={() => setShowRemoveUserPrompt(false)}
        heading={"Remove user"}
        visible={showRemoveUserPrompt}
        bottomButtons={[
          <Button
            variant="filled"
            buttonText="Cancel"
            icon={{}}
            disabled={false}
            isLoading={false}
            hierarchy="white"
            onClick={() => setShowRemoveUserPrompt(false)}
          />,
          <Button
            variant="filled"
            buttonText={"Remove"}
            icon={{}}
            isLoading={false}
            hierarchy="red"
            disabled={false}
            onClick={() => updateOutboundUsers( "remove", rowBeingEdited.id)}
          />,
        ]}
        body={
          <div className="t6 regular-font">
            Are you sure you want to remove&nbsp;
            {!!rowBeingEdited && (
              <p
                style={{
                  display: "inline",
                  textDecoration: "underline",
                  fontWeight: "500",
                }}
              >
                {rowBeingEdited.first_name + " " + rowBeingEdited.last_name}
              </p>
            )}
            &nbsp;from outgoing users of&nbsp;
            <p style={localStyles.number}>{originalSettings.number}</p>
            &nbsp; ?
          </div>
        }
      />
    </>
  );
}

const localStyles = {
	container: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},
	addUserModalStyle: {
		width: "507px",
		minHeight: "240px",
		paddingTop: "8px",
    marginBottom:'0'
	},
	
	userSelect: {
		...commonStyles.select,
		width: "400px",
		marginTop: "4px",
	},
	userMenuItem: {
		"&.MuiMenuItem-root.Mui-selected": {
			background: "#F0FCF4",
		},
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		width: "100%",
	},

  removeUserModalStyle: {
    width: "448px",
    height: "128px",
  },

  number: {
    display: "inline",
    textDecoration: "underline",
    fontWeight: "500",
  },

  pNoOverflow: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "360px",
    margin: 0,
  },
  buttonStyles: {
		height: "40px",
		gap: "4px",
		borderRadius: "6px",
		paddingTop: "10px",
		paddingRight: "14px",
		paddingBottom: "10px",
		paddingLeft: "14px"
	},
  usersContainer: {
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
    marginTop: '12px',
    maxHeight: '198px',
    overflowY: 'scroll',
    minHeight: '120px'
  },
  flexRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row'
  },
};
